import React, { useState } from 'react';
import { Input, Button, Space, message, Form, Tag, Checkbox, Row, Col, Modal, Tooltip, Alert, Divider, Typography } from 'antd';
import { EditOutlined, KeyOutlined, UserAddOutlined, LockOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';
import { MODULE_IDS } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';

import { getUsers, createUser, updateUser, resetPassword, getAllModules } from '../../api/users.api';

import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import FormDrawer from '../../components/common/FormDrawer';

const UserManagementPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetModal, setResetModal] = useState({ open: false, loginId: null });
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const { Text } = Typography;

  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();
  const { canAdd, canEdit } = usePermissions(MODULE_IDS.USER_MGMT);

  const { control, handleSubmit, reset, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      loginID: '', user_Name: '', password: '', mobileNo: '', isActive: true, moduleList: []
    }
  });

  // --- Queries ---
  const { data: usersResponse, isLoading: isUsersLoading } = useQuery({
    queryKey: ['users', plantId],
    queryFn: () => getUsers(plantId),
    enabled: !!plantId
  });

  const usersData = usersResponse?.items || [];

  const { data: modulesData } = useQuery({
    queryKey: ['allModules'],
    queryFn: getAllModules
  });

  // --- Mutations ---
  const saveMutation = useMutation({
    mutationFn: (data) => editingUser 
      ? updateUser(plantId, editingUser.loginID, data) 
      : createUser(plantId, data),
    onSuccess: () => {
      message.success(`User ${editingUser ? 'updated' : 'created'} successfully!`);
      queryClient.invalidateQueries(['users']);
      closeDrawer();
    },
    onError: (err) => message.error(err.message || "Operation failed")
  });

  const resetPassMutation = useMutation({
    mutationFn: () => resetPassword(plantId, resetModal.loginId, resetPasswordValue),
    onSuccess: () => {
      message.success('Password has been reset successfully');
      setResetModal({ open: false, loginId: null });
      setResetPasswordValue('');
    }
  });

  // --- Handlers ---
  const openDrawer = (record = null) => {
    setEditingUser(record);
    if (record) {
      reset({
        loginID: record.loginID,
        user_Name: record.user_Name,
        password: '',
        mobileNo: record.mobileNo || '',
        isActive: record.isActive,
        moduleList: record.moduleList || []
      });
    } else {
      reset({ loginID: '', user_Name: '', password: '', mobileNo: '', isActive: true, moduleList: [] });
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingUser(null);
  };

  const onSubmit = (data) => saveMutation.mutate(data);

  // --- Columns ---
  const columns = [
    { title: 'Login ID', dataIndex: 'loginID', key: 'loginID', render: (text) => <b>{text}</b> },
    { title: 'User Name', dataIndex: 'user_Name', key: 'user_Name' },
    { title: 'Mobile', dataIndex: 'mobileNo', key: 'mobileNo', render: v => v || '-' },
    { 
      title: 'Module Access', 
      dataIndex: 'moduleList', 
      render: (list) => <Tag color="blue">{list?.length || 0} Modules</Tag> 
    },
    { title: 'Created', dataIndex: 'createdOn', render: v => formatDate(v) },
    { 
      title: 'Status', 
      dataIndex: 'isActive', 
      render: (active) => <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag> 
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canEdit && (
            <>
              <Tooltip title="Edit User & Access">
                <Button type="text" icon={<EditOutlined style={{ color: '#1890ff' }} />} onClick={() => openDrawer(record)} />
              </Tooltip>
              <Tooltip title="Reset Password">
                <Button 
                  type="text" 
                  icon={<KeyOutlined style={{ color: '#faad14' }} />} 
                  onClick={() => setResetModal({ open: true, loginId: record.loginID })} 
                />
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader 
        title="User Management" 
        subtitle="Control who can access the system and which modules are visible to them"
        onAdd={() => openDrawer()} 
        canAdd={canAdd}
        addLabel="Create New User"
      />

      <DataTable 
        columns={columns} 
        data={usersData || []} 
        loading={isUsersLoading} 
        rowKey="loginID" 
      />

      {/* FORM DRAWER */}
      <FormDrawer
        title={editingUser ? "Edit User Access" : "Create New User"}
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={handleSubmit(onSubmit)}
        loading={saveMutation.isPending}
        isDirty={isDirty}
        width={550}
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Login ID" required help={errors.loginID?.message} validateStatus={errors.loginID ? 'error' : ''}>
                <Controller
                  name="loginID"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => <Input {...field} disabled={!!editingUser} placeholder="e.g. rahul_admin" />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Full Name" required help={errors.user_Name?.message} validateStatus={errors.user_Name ? 'error' : ''}>
                <Controller
                  name="user_Name"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => <Input {...field} placeholder="Enter user's name" />}
                />
              </Form.Item>
            </Col>
          </Row>

          {!editingUser && (
            <Form.Item label="Initial Password" required help={errors.password?.message} validateStatus={errors.password ? 'error' : ''}>
              <Controller
                name="password"
                control={control}
                rules={{ required: 'Password is required for new users' }}
                render={({ field }) => <Input.Password {...field} prefix={<LockOutlined />} />}
              />
            </Form.Item>
          )}

          <Form.Item label="Mobile Number" help={errors.mobileNo?.message} validateStatus={errors.mobileNo ? 'error' : ''}>
            <Controller
              name="mobileNo"
              control={control}
              rules={{ pattern: { value: /^[0-9]{10}$/, message: '10 digits only' } }}
              render={({ field }) => <Input {...field} maxLength={10} placeholder="10-digit mobile" />}
            />
          </Form.Item>

          <Divider orientation="left">Module Access Rights</Divider>
          <Alert 
            title="Check the modules this user is allowed to see in their Sidebar menu." 
            type="info" showIcon style={{ marginBottom: 16 }} 
          />
          
          <Form.Item name="moduleList">
            <Controller
              name="moduleList"
              control={control}
              render={({ field }) => (
                <Checkbox.Group {...field} style={{ width: '100%' }}>
                  <Row gutter={[16, 16]}>
                    {modulesData?.map(mod => (
                      <Col span={12} key={mod.mid}>
                        <Checkbox value={mod.mid}>{mod.modules}</Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              )}
            />
          </Form.Item>
        </Form>
      </FormDrawer>

      {/* RESET PASSWORD MODAL */}
      <Modal
        title={<span><KeyOutlined /> Reset User Password</span>}
        open={resetModal.open}
        onOk={() => resetPassMutation.mutate()}
        onCancel={() => setResetModal({ open: false, loginId: null })}
        confirmLoading={resetPassMutation.isPending}
        okText="Update Password"
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">Resetting password for: <b>{resetModal.loginId}</b></Text>
        </div>
        <Alert 
          title="Security Note" 
          description="Passwords are encrypted and cannot be recovered. Setting a new one is the only way if forgotten." 
          type="warning" showIcon style={{ marginBottom: 16 }} 
        />
        <Input.Password 
          placeholder="Enter new strong password" 
          value={resetPasswordValue}
          onChange={(e) => setResetPasswordValue(e.target.value)}
          prefix={<LockOutlined />}
        />
      </Modal>
    </div>
  );
};

export default UserManagementPage;